<template>
  <div class="modal-backdrop">
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>{{ $t('bo.billing.walletDetail') }}</h2>
          <p v-if="wallet">{{ wallet.company?.name }}</p>
        </div>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <div class="modal-body">
      <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>
      <div v-else-if="error" class="error-msg">{{ error }}</div>

      <template v-else-if="wallet">
        <!-- Balance summary -->
        <div class="balance-card">
          <div class="balance-main">
            <span :class="['balance-amount', { low: isLow }]">{{ wallet.balance }}</span>
            <span class="balance-currency">{{ wallet.currency }}</span>
          </div>
          <div class="balance-meta">
            <span>{{ $t('bo.billing.threshold') }}: {{ wallet.low_balance_threshold ?? 5 }}</span>
            <span v-if="isLow" class="low-label">⚠ {{ $t('bo.billing.lowBalance') }}</span>
          </div>
          <button @click="showAdjust = true" class="btn-adjust">
            {{ $t('bo.billing.adjust') }}
          </button>
        </div>

        <!-- Recent transactions -->
        <div class="section">
          <h3>{{ $t('bo.billing.recentTransactions') }}</h3>
          <div v-if="transactions.length === 0" class="empty-section">{{ $t('bo.billing.noTransactions') }}</div>
          <table v-else class="inner-table">
            <thead>
              <tr>
                <th>{{ $t('bo.billing.colType') }}</th>
                <th>{{ $t('bo.billing.colAmount') }}</th>
                <th>{{ $t('bo.billing.colBalanceAfter') }}</th>
                <th>{{ $t('bo.billing.colDescription') }}</th>
                <th>{{ $t('bo.billing.colDate') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in transactions" :key="tx.id">
                <td><span :class="['type-badge', `type-${tx.type}`]">{{ tx.type }}</span></td>
                <td :class="['col-amount', tx.amount > 0 ? 'positive' : 'negative']">
                  {{ tx.amount > 0 ? '+' : '' }}{{ tx.amount }}
                </td>
                <td>{{ tx.balance_after }}</td>
                <td class="col-desc">{{ tx.description || '—' }}</td>
                <td class="col-date">{{ formatDate(tx.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Recent purchases -->
        <div class="section">
          <h3>{{ $t('bo.billing.recentPurchases') }}</h3>
          <div v-if="purchases.length === 0" class="empty-section">{{ $t('bo.billing.noPurchases') }}</div>
          <table v-else class="inner-table">
            <thead>
              <tr>
                <th>{{ $t('bo.billing.colCredits') }}</th>
                <th>{{ $t('bo.billing.colPrice') }}</th>
                <th>{{ $t('bo.billing.colPaymentStatus') }}</th>
                <th>{{ $t('bo.billing.colReference') }}</th>
                <th>{{ $t('bo.billing.colDate') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in purchases" :key="p.id">
                <td><strong>{{ p.credit_amount }}</strong></td>
                <td>{{ (p.price_amount / 100).toFixed(2) }} {{ p.currency }}</td>
                <td><span :class="['status-badge', `status-${p.payment_status}`]">{{ p.payment_status }}</span></td>
                <td class="col-ref">{{ p.payment_reference || '—' }}</td>
                <td class="col-date">{{ formatDate(p.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      </div>

      <AdjustModal
        v-if="showAdjust && wallet"
        :company-id="companyId"
        @close="showAdjust = false"
        @adjusted="onAdjusted"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import billingService from '@/services/billingService'
import AdjustModal from './AdjustModal.vue'

const props = defineProps({ companyId: { type: Number, required: true } })
const emit = defineEmits(['close', 'adjusted'])
const { t } = useI18n()

const wallet = ref(null)
const transactions = ref([])
const purchases = ref([])
const loading = ref(false)
const error = ref('')
const showAdjust = ref(false)

const isLow = computed(() => wallet.value && wallet.value.balance <= (wallet.value.low_balance_threshold ?? 5))

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await billingService.getWallet(props.companyId)
    const data = res.data?.data
    wallet.value = data?.wallet
    transactions.value = data?.transactions || []
    purchases.value = data?.purchases || []
  } catch {
    error.value = t('bo.billing.failedLoad')
  } finally {
    loading.value = false
  }
}

const onAdjusted = () => {
  showAdjust.value = false
  load()
  emit('adjusted')
}

const formatDate = (d) => d ? new Date(d).toLocaleString() : '—'

onMounted(load)
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 2rem 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 860px;
  max-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem;
}

.modal-header p { color: #6b7280; font-size: 0.875rem; margin: 0; }

.btn-close {
  background: none;
  border: none;
  font-size: 1rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.loading, .empty-section {
  padding: 1.5rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.error-msg { color: #dc2626; padding: 1rem; }

.balance-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.25rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.balance-main { display: flex; align-items: baseline; gap: 0.5rem; }

.balance-amount {
  font-size: 2rem;
  font-weight: 800;
  color: #1f2937;
}

.balance-amount.low { color: #dc2626; }
.balance-currency { font-size: 1rem; color: #6b7280; }

.balance-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.low-label { color: #dc2626; font-weight: 600; }

.btn-adjust {
  margin-left: auto;
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-adjust:hover { background: #2563eb; }

.section { margin-bottom: 1.5rem; }

.section h3 {
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
}

.inner-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}

.inner-table th {
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
}

.inner-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.inner-table tr:last-child td { border-bottom: none; }

.type-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.type-purchase { background: #d1fae5; color: #065f46; }
.type-debit { background: #fee2e2; color: #991b1b; }
.type-refund { background: #dbeafe; color: #1e40af; }
.type-adjustment { background: #f3e8ff; color: #6b21a8; }

.col-amount { font-weight: 700; }
.positive { color: #059669; }
.negative { color: #dc2626; }
.col-desc { color: #6b7280; max-width: 200px; }
.col-date { color: #9ca3af; white-space: nowrap; }
.col-ref { font-family: monospace; font-size: 0.7rem; color: #6b7280; }

.status-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.status-pending { background: #fef3c7; color: #92400e; }
.status-succeeded { background: #d1fae5; color: #065f46; }
.status-failed { background: #fee2e2; color: #991b1b; }
.status-cancelled { background: #f3f4f6; color: #6b7280; }
</style>
